import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/instrument";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { mutationResponse } from "@/utils/trpc";
import type { InstrumentDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof createSchema>;

export function InstrumentDetailForm({
  instrument,
}: {
  instrument?: InstrumentDetail;
}) {
  const create = trpc.admin.instrument.create.useMutation();
  const update = trpc.admin.instrument.update.useMutation();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      ...instrument,
      model: instrument?.model || "",
      manufacturer: instrument?.manufacturer || "",
    },
    resolver: zodResolver(createSchema),
  });

  const integrations = trpc.admin.instrument.listIntegrations.useQuery();
  const integrationOptions =
    integrations.data?.map<{ label: string; value: string }>((integration) => ({
      label: integration,
      value: integration,
    })) ?? [];

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (instrument) {
      update.mutate(
        { id: instrument.id, ...values },
        mutationResponse({
          errorTitle: "Error updating instrument",
          successMessage: "Instrument updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding instrument",
          successMessage: "Instrument added",
          onSuccess(data) {
            navigate({
              to: "/admin/instruments/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="model"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Model</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="manufacturer"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Manufacturer</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="serialNumber"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Serial number</Label>
                <Input />
                <Description>
                  Optional. This is the serial number of the instrument for
                  identification purposes
                </Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="ref"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Select
                {...field}
                selectedKey={field.value}
                onSelectionChange={(value) => {
                  field.onChange(value === -1 ? null : value);
                }}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Integration ID</Label>
                <SelectTrigger isClearable />
                <SelectList items={integrationOptions}>
                  {(item) => (
                    <SelectItem id={item.value}>{item.label}</SelectItem>
                  )}
                </SelectList>
                <Description>
                  This is the ID used by the integration to identify this
                  instrument. Provided by your bLIS admin
                </Description>
                <FieldError>{error?.message}</FieldError>
              </Select>
            )}
          />
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Link to="/admin/instruments" variant="outline" asButton>
            Cancel
          </Link>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
          >
            Save
          </Button>
        </div>
      </AdminForm>
    </TabLayout>
  );
}
